import { GetObjectCommand, S3Client } from "@aws-sdk/client-s3";
import { XhrHttpHandler } from "@aws-sdk/xhr-http-handler";
import { AWS_REGION, AWSCredentials, CHAT_HISTORY_S3_BUCKET } from "constants/aws";
import { ENABLE_PUBNUB_S3_HYBRID_HISTORY } from "constants/featureFlags";
import DOMPurify from "dompurify";
import type PubNub from "pubnub";
import queryTranscriptionBucketTempCredentials from "services/transcription/queryTranscriptionBucketTempCredentials";
import { reportProblem } from "utils/frontend_error_reporting";
import { ChatMessageData, FetchHybridHistoryParams, S3ChannelData } from "../pubnub.types";
import chatMessageDecrypt from "../utils/chat-message-decrypt";
import pubNubTimetokenToDate from "../utils/pubnub-timetoken/pubnub-timetoken-to-date";

interface FetchPubnubHistoryArgs {
  pubNubInstance: PubNub;
  channel: string;
  privateChatCipherKey: string;
  token: string;
  onlyOlderThanTimetoken?: string;
  onlyNewerThanTimetoken?: string;
  limit?: number;
}

/**
 * Creates an S3 client with temporary credentials
 */
const getS3Client = (credentials: FetchHybridHistoryParams['credentials']) => {
  return new S3Client({
    region: AWS_REGION,
    credentials: {
      accessKeyId: credentials.accessKeyId,
      secretAccessKey: credentials.secretAccessKey,
      sessionToken: credentials.sessionToken,
    },
    requestHandler: new XhrHttpHandler({}),
  });
};

/**
 * Fetches messages from S3 for a specific channel
 */
const fetchFromS3 = async (
  s3Client: S3Client,
  channel: string
): Promise<S3ChannelData | null> => {
  try {
    const response = await s3Client.send(
      new GetObjectCommand({
        Bucket: CHAT_HISTORY_S3_BUCKET,
        Key: `${channel}.json`,
      })
    );

    if (!response.Body) return null;

    const data = await response.Body.transformToString();
    return JSON.parse(data) as S3ChannelData;
  } catch (error) {
    // If the file doesn't exist, return null instead of throwing
    if ((error as Error).name === 'NoSuchKey') {
      return null;
    }
    console.error('Error fetching from S3:', error);
    throw error;
  }
};

/**
 * Fetches temporary AWS credentials for the transcription bucket
 */
const getAWSCredentials = async () => {
  const awsCredentialsJSON = await queryTranscriptionBucketTempCredentials();
  const awsCredentials: AWSCredentials = {
    accessKeyId: awsCredentialsJSON.access_key_id,
    secretAccessKey: awsCredentialsJSON.secret_access_key,
    sessionToken: awsCredentialsJSON.session_token,
  };
  return awsCredentials;
}

const getMessagesFromS3 = async (
  s3Client: S3Client,
  channel: string
): Promise<{ messages: [], lastSynced: string | undefined }> => {
  const s3Data = await fetchFromS3(s3Client, channel);
  let s3Messages = [];
  let lastSynced;
  if (s3Data) {
    s3Messages = s3Data?.messages;
    lastSynced = s3Data.metadata.lastSynced;
  }
  return {
    messages: s3Messages,
    lastSynced,
  }
}

const fetchPubnubHistory = async ({
  pubNubInstance,
  channel,
  privateChatCipherKey,
  token,
  onlyOlderThanTimetoken,
  limit,
}: FetchPubnubHistoryArgs): Promise<Array<ChatMessageData>> => {
  const awsCredentials = await getAWSCredentials();
  const s3Client = getS3Client(awsCredentials);
  const s3Messages: ChatMessageData[] = [];

  const messages: Array<ChatMessageData> = [];

  if (ENABLE_PUBNUB_S3_HYBRID_HISTORY) {
    const { messages: messagesFromS3 } = await getMessagesFromS3(s3Client, channel);
    s3Messages.push(...messagesFromS3);
  }

  try {
    // console.time(`fetch pubnub history ${channel}`);
    pubNubInstance.setToken(token);
    pubNubInstance.setCipherKey(privateChatCipherKey);
    const body = {
      channels: [channel],
      // https://www.pubnub.com/docs/chat/sdks/messages/message-history
      count: limit,
      includeMessageActions: true,
      includeMeta: true,
      start: onlyOlderThanTimetoken,
    };
    const fetchMessagesPromise = pubNubInstance.fetchMessages(body);

    const listFilesPromise = pubNubInstance.listFiles({ channel });

    const [fetchMessagesResult, listFilesResult] = await Promise.all([
      fetchMessagesPromise,
      listFilesPromise,
    ]);

    // console.timeEnd(`fetch pubnub history ${channel}`);
    const channelResult = fetchMessagesResult.channels[channel] || [];
    if (channelResult) {
      const combinedResults = [...channelResult, ...s3Messages];
      const seenTimestamp = new Set();
      for (const entry of combinedResults) {
        if (seenTimestamp.has(entry.timetoken)) {
          continue;
        }
        seenTimestamp.add(entry.timetoken);
        // assume there is only one reaction
        const reactionEmoji =
          entry.actions && entry.actions.reaction
            ? Object.keys(entry.actions.reaction)[0]
            : null;
        const reaction = reactionEmoji
          ? {
              actionTimetoken:
                entry.actions.reaction[
                  reactionEmoji
                ][0].actionTimetoken.toString(),
              emoji: reactionEmoji,
            }
          : null;
        let message;
        if (entry.encrypted) {
          message = chatMessageDecrypt(pubNubInstance, entry.message, privateChatCipherKey);
          entry.message = message;
        } else {
          message = entry.message.file
            ? entry.message?.message
            : entry.message;
        }
        let contentSanitized: string | undefined = undefined;
        if (message && typeof message === 'string' && message.startsWith('{') && message.endsWith('}')) {
          message = JSON.parse(message);
        }
        if (!message?.content) {
          const msg = chatMessageDecrypt(
                pubNubInstance,
                message,
                privateChatCipherKey,
              );
          if (!msg && !entry.message.file) {
            await reportProblem({
              reporter_module: "OrchidPubNubStateContext/pubnub_fetch_history",
              context: {},
              frontend_error: "failed to decrypt message entry",
            });
            continue;
          }
          contentSanitized = DOMPurify.sanitize(msg.content) as string;
        } else {
          contentSanitized = DOMPurify.sanitize(message.content) as string;
        }
        const sender = entry.meta?.sender_cognito_sub;
        if (!sender) {
          await reportProblem({
            reporter_module: "OrchidPubNubStateContext/pubnub_fetch_history",
            context: {},
            frontend_error: "no sender metadata in entry",
          });
          continue;
        }

        const receiptsByCognitoSub = {};
        if (entry?.actions?.receipt) {
          for (const [cognitoSub, d] of Object.entries(entry.actions.receipt)) {
            receiptsByCognitoSub[cognitoSub] = pubNubTimetokenToDate(
              d[0].actionTimetoken.toString(),
            );
          }
        }

        if (entry.message.file) {
          const fileMetaData = listFilesResult.data.find(
            (e) => e.id === entry.message.file.id,
          );
          messages.push({
            msgUniqueId: entry?.meta?.msg_unique_id || "",
            sender,
            messageTimetoken: entry.timetoken.toString(),
            content: contentSanitized,
            file: {
              fileName: `${entry.message.file.name}`,
              fileId: entry.message.file.id,
              size: fileMetaData?.size,
            },
            reaction,
            receiptsByCognitoSub,
          });
        } else if (entry.messageType === -1 || entry.messageType === null) {
          messages.push({
            msgUniqueId: entry.meta?.msg_unique_id || "",
            sender,
            messageTimetoken: entry.timetoken.toString(),
            content: contentSanitized,
            file: undefined,
            reaction: reaction,
            receiptsByCognitoSub,
          });
        } else {
          await reportProblem({
            reporter_module: "OrchidPubNubStateContext/pubnub_fetch_history",
            context: {},
            frontend_error: "unknown, unhandled message entry",
          });
        }
      }
    }
  } catch (error) {
    console.error('Error fetching messages from PubNub:', error);
  }

  return messages.slice(0, limit);
};

export default fetchPubnubHistory;
